<template>
  <div class="nepali-calendar-container">
    <div class="calendar-controls">
      <select v-model="selectedYear" @change="fetchCalendarData">
        <option v-for="year in availableYears" :key="year" :value="year">
          {{ year }}
        </option>
      </select>

      <div class="calendar-navigation">
        <button @click="previousMonth">&lt;</button>
        <h2>{{ currentMonthName }} {{ selectedYear }}</h2>
        <button @click="nextMonth">&gt;</button>
      </div>
    </div>

    <div v-if="loading" class="loading">क्यालेन्डर डाटा लोड हुँदैछ...</div>

    <div v-else-if="error" class="error">
      {{ error }}
    </div>

    <div v-else class="calendar-wrapper">
      <div class="calendar-header">
        <div class="weekdays">
          <div
            v-for="(day, index) in weekdays"
            :key="index"
            :class="['weekday', { weekend: index === 6 }]"
          >
            {{ day }}
          </div>
        </div>
      </div>

      <div class="calendar-grid">
        <!-- Empty slots before first day -->
        <div
          v-for="n in emptySlotsBefore"
          :key="`empty-${n}`"
          class="calendar-cell empty"
        ></div>

        <!-- Calendar days -->
        <div
          v-for="(day, index) in currentMonthDays"
          :key="index"
          class="calendar-cell"
          :class="{
            'current-day': isCurrentDay(day),
            'has-events': day.events && day.events.length > 0,
          }"
          @click="openDayDialog(day)"
        >
          <div class="day-content">
            <div class="day-en">{{ day.en }}</div>
            <div class="day-np" :style="{ color: getDayColor(day) }">
              {{ convertToNepaliDigits(day.dayInEn) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import NepaliDate from "nepali-date";

export default {
  name: "NepaliCalendar",
  data() {
    const nepaliDate = new NepaliDate();
    return {
      nepaliDate: {
        year: nepaliDate.getYear(),
        month: nepaliDate.getMonth() + 1,
        day: nepaliDate.getDate(),
      },
      availableYears: [
        2070, 2071, 2072, 2073, 2074, 2075, 2076, 2077, 2078, 2079, 2080, 2081,
        2082, 2083, 2084, 2085, 2086, 2087, 2088, 2089, 2090,
      ],
      selectedYear: nepaliDate.getYear(),
      currentMonth: nepaliDate.getMonth() + 1,
      calendarData: [],
      loading: true,
      error: null,
      weekdays: ["आइत", "सोम", "मंगल", "बुध", "बिही", "शुक्र", "शनि"],
      dayToIndex: {
        Sunday: 0,
        Monday: 1,
        Tuesday: 2,
        Wednesday: 3,
        Thursday: 4,
        Friday: 5,
        Saturday: 6,
      },
      monthNames: {
        1: "बैशाख",
        2: "जेठ",
        3: "असार",
        4: "श्रावण",
        5: "भदौ",
        6: "आश्विन",
        7: "कार्तिक",
        8: "मंसिर",
        9: "पुष",
        10: "माघ",
        11: "फाल्गुन",
        12: "चैत्र",
      },
      currentTime: new Date(),
    };
  },
  computed: {
    currentMonthName() {
      return this.monthNames[this.currentMonth];
    },
    currentMonthDays() {
      const monthData = this.calendarData.find(
        (month) => month.month === this.currentMonth
      );

      return monthData ? monthData.days : [];
    },
    emptySlotsBefore() {
      if (this.currentMonthDays.length === 0) return 0;

      // Find the first day of the month to determine empty slots
      const firstDay = this.currentMonthDays[0];
      return this.dayToIndex[firstDay.day];
    },
  },
  methods: {
    async fetchCalendarData() {
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get(
          `https://calander.cellapp.co/api/get-calendar/${this.selectedYear}`
        );

        if (response.data && response.data.data) {
          this.calendarData = response.data.data;
          this.loading = false;
        } else {
          throw new Error("Invalid calendar data");
        }
      } catch (error) {
        this.error = `Failed to fetch calendar data: ${error.message}`;
        this.loading = false;
      }
    },
    convertToNepaliDigits(number) {
      const nepaliDigits = ["०", "१", "२", "३", "४", "५", "६", "७", "८", "९"];
      return String(number)
        .split("")
        .map((digit) => nepaliDigits[parseInt(digit)])
        .join("");
    },
    isCurrentDay(day) {
      return (
        this.currentMonth === this.nepaliDate.month &&
        day.dayInEn.toString() === this.nepaliDate.day.toString()
      );
    },
    getDayColor(day) {
      return this.isCurrentDay(day) ? "white" : day.color;
    },
    openDayDialog(day) {
      if (day.events && day.events.length > 0) {
        this.$emit("open-day-dialog", day);
      } else {
        this.$emit("open-empty-day-dialog", day);
      }
    },
    previousMonth() {
      this.currentMonth = this.currentMonth === 1 ? 12 : this.currentMonth - 1;
    },
    nextMonth() {
      this.currentMonth = this.currentMonth === 12 ? 1 : this.currentMonth + 1;
    },
  },
  created() {
    this.fetchCalendarData();
  },
};
</script>

<style scoped>
.nepali-calendar-container {
  max-width: 600px;
  margin: 0 auto;
  font-family: sans-serif;
  background-color: #f9f9f9;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.calendar-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background-color: #5c5c5c;
  color: white;
}

.calendar-controls select {
  background-color: #fff;
  color: #000;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  outline: none;
  cursor: pointer;
}

.calendar-navigation {
  display: flex;
  align-items: center;
  gap: 15px;
}

.calendar-navigation h2 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 550;
  color: #fff;
}

.calendar-navigation button {
  background-color: #f99743;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.calendar-navigation button:hover {
  background-color: #e88532;
  transform: scale(1);
}

.weekdays {
  display: flex;
  background-color: #30b78a;
}

.weekday {
  flex: 1;
  text-align: center;
  padding: 12px;
  color: white;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.weekday.weekend {
  background-color: rgb(200, 59, 59);
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  padding: 8px;
}

.calendar-cell {
  aspect-ratio: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  background-color: white;
  border-radius: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid #e0e0e0;
}

.calendar-cell:hover {
  background-color: rgba(48, 183, 138, 0.1);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.calendar-cell.current-day {
  background-color: #30b78a;
  color: white;
  font-weight: bold;
}

.calendar-cell.empty {
  background-color: transparent;
  border: none;
  cursor: default;
}

.day-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: relative;
}

.day-en {
  font-size: 0.5rem;
  color: #5c5c5c;
  position: absolute;
  top: 4px;
  right: 4px;
  opacity: 0.7;
}

.day-np {
  font-size: 1.5rem;
  font-weight: 500;
}

.loading,
.error {
  text-align: center;
  padding: 30px;
  color: #5c5c5c;
  background-color: #f0f0f0;
}
</style>
